import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { listPendingProgress, subscribeDeviceProgress } from "../../utils/deviceProgress";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { useProgressSync } from "../../hooks/useProgressSync";
import { isDesktopLocalProfile } from "../../utils/desktopProfile";
import styles from "./deviceProgressStatus.module.scss";

export default function DeviceProgressStatus() {
    const { status, user } = useAuth();
    const owner = status === "authenticated" ? user?.identityId : status === "unauthenticated" ? "guest" : undefined;
    const sync = useProgressSync(owner);
    const { t } = useTranslation("common");
    const lp = useLocalizedPath();
    const [state, setState] = useState<{ owner?: string; pending: number; error: boolean }>({ pending: 0, error: false });
    useEffect(() => {
        if (!owner) return;
        let cancelled = false;
        const refresh = () => listPendingProgress(owner).then(rows => {
            if (!cancelled) setState(current => ({ owner, pending: rows.length, error: current.owner === owner && current.error }));
        }).catch(() => { if (!cancelled) setState({ owner, pending: 0, error: true }); });
        const onError = (event: Event) => {
            if ((event as CustomEvent).detail === owner && !cancelled) setState(current => ({ ...current, owner, error: true }));
        };
        void refresh();
        const unsubscribe = subscribeDeviceProgress(id => { if (id === owner) void refresh(); });
        window.addEventListener("nihongotech-device-save-error", onError);
        return () => { cancelled = true; unsubscribe(); window.removeEventListener("nihongotech-device-save-error", onError); };
    }, [owner]);
    if (!owner || state.owner !== owner || (!state.pending && !state.error && sync.status !== "error")) return null;
    return <div className={styles.status} role={state.error || sync.status === "error" ? "alert" : "status"}>
        {t(state.error ? "deviceProgress.error" : sync.status === "error" ? "deviceProgress.syncError" : sync.status === "syncing" ? "deviceProgress.syncing" : "deviceProgress.pending")}
        {!isDesktopLocalProfile && <>{" "}<Link to={lp("/offline")}>{t("deviceProgress.details")}</Link></>}
        {state.pending > 0 && !state.error && <button disabled={sync.status === "syncing"} onClick={sync.retry}>{t("deviceProgress.syncNow")}</button>}
        {state.error && <button onClick={() => { setState(current => ({ ...current, error: false })); window.dispatchEvent(new CustomEvent("nihongotech-device-retry", { detail: owner })); }}>{t("deviceProgress.retry")}</button>}
    </div>;
}
